import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { InventoryReport } from '@shared/definitions/inventory-report';
import { InventoryReportService } from '@core/services/data/inventory-report.service';

/**
 * Signal-based store for inventory reports ("état des lieux"). Mirrors
 * ContractStore: the service bridges its entities stream into `setList`, and
 * write actions delegate back to the service via a lazy Injector lookup to
 * avoid the circular DI (service injects store for the read bridge).
 */
@Injectable({ providedIn: 'root' })
export class InventoryReportStore {
  private readonly _list = signal<readonly InventoryReport[]>([]);

  private readonly injector = inject(Injector);
  private _service: InventoryReportService | null = null;
  private get service(): InventoryReportService {
    return (this._service ??= this.injector.get(InventoryReportService));
  }

  readonly list = this._list.asReadonly();
  readonly count = computed(() => this._list().length);

  /** Reactive list of reports for a contract. */
  readonly byContract = (contractUID: string) =>
    computed(() => this._list().filter(r => r.contractUID === contractUID));

  /** Synchronous lookup. */
  getById(reportUID: string): InventoryReport | null {
    return this._list().find(r => r.reportUID === reportUID) ?? null;
  }

  getForContract(contractUID: string): readonly InventoryReport[] {
    return this._list().filter(r => r.contractUID === contractUID);
  }

  setList(reports: readonly InventoryReport[]): void {
    this._list.set(reports);
  }

  upsert(report: InventoryReport): void {
    const list = this._list();
    const i = list.findIndex(r => r.reportUID === report.reportUID);
    if (i === -1) {
      this._list.set([...list, report]);
    } else {
      const next = list.slice();
      next[i] = report;
      this._list.set(next);
    }
  }

  /**
   * Create a new inventory report; resolves to the new reportUID.
   * `reportUID` + `workspaceId` are stamped by the service on create.
   */
  create(report: Omit<InventoryReport, 'reportUID' | 'workspaceId'>): Observable<string> {
    return this.service.createEntity(report);
  }

  /** Patch an existing report. */
  update(reportUID: string, patch: Partial<InventoryReport>): Observable<void> {
    return this.service.updateEntity(reportUID, patch);
  }

  /** Delete a report. */
  remove(reportUID: string): Observable<void> {
    return this.service.deleteEntity(reportUID);
  }
}
